import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from 'lib/utils';
import { SidebarStoreSelector } from './sidebar-store-selector';
import { SidebarSection } from './sidebar-section';
import { SidebarFooter } from './sidebar-footer';
import { useSidebar } from './useSidebar';
import useAuth from 'state/auth/hooks/useAuth';
import { HOVER_CLOSE_DELAY } from './nav-item-constants';
import type { SidebarProps } from './types';

const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  ({ className, ...props }, ref) => {
    const { isHovered, isCollapsed, sections, setHovered } = useSidebar();
    const location = useLocation();
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { auth } = useAuth();
    const isNativeMode = Boolean(auth?.isNativeMode);

    const isExpanded = !isNativeMode && (isHovered || isDropdownOpen);

    const handleMouseEnter = useCallback(() => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setHovered(true);
    }, [setHovered]);

    const handleMouseLeave = useCallback(() => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setHovered(false);
        hoverTimeoutRef.current = null;
      }, HOVER_CLOSE_DELAY);
    }, [setHovered]);

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    // Collapse the hover-expanded overlay on navigation. The <aside> is
    // position:fixed and, when expanded (z-40, w-64), floats over the main
    // content — the layout reserves only the collapsed 72px rail in flow. Hover
    // state otherwise clears on mouseleave, so after a nav-item click the
    // expanded overlay can linger over the page and intercept pointer events on
    // the content beneath it (e.g. the first settings tab) until the pointer
    // happens to leave. Releasing it on route change frees the page the user
    // just navigated to.
    useEffect(() => {
      // Cancel any pending mouseleave debounce so a stale timer can't fire a
      // redundant state update after we've already collapsed here.
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setHovered(false);
      setIsDropdownOpen(false);
    }, [location.pathname, setHovered, setIsDropdownOpen]);

    return (
      <div className="hidden md:block w-18 shrink-0">
        <aside
          ref={ref}
          className={cn(
            'w-[72px] transition-[width] duration-200 ease-in-out',
            'flex flex-col',
            'bg-white border-r border-gray-blue-10',
            'overflow-x-hidden overflow-y-auto',
            'h-[calc(100vh-4rem)] fixed top-16 left-0 z-30',
            'motion-reduce:transition-none',
            isExpanded && 'w-64 shadow-lg z-40',
            className
          )}
          onMouseEnter={isNativeMode ? undefined : handleMouseEnter}
          onMouseLeave={isNativeMode ? undefined : handleMouseLeave}
          role="navigation"
          aria-label="Main navigation"
          {...props}
        >
          <div className="flex flex-1 flex-col justify-between h-full py-4">
            <div className="flex-1">
              {sections.map((section) => (
                <SidebarSection
                  key={section.id}
                  section={section}
                  isCollapsed={isCollapsed && !isExpanded}
                />
              ))}
            </div>
            <div>
              <SidebarStoreSelector
                isCollapsed={isCollapsed && !isExpanded}
                onOpenChange={setIsDropdownOpen}
              />
              <SidebarFooter isCollapsed={isCollapsed && !isExpanded} />
            </div>
          </div>
        </aside>
      </div>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
export { Sidebar };
export type { SidebarProps, SidebarNavItem, SidebarSection } from './types';
